import React, {useCallback, useEffect, useRef, useState} from 'react';
import {RouteProps, Routes, useLocation, useNavigate} from 'react-router-dom';
import styled, {keyframes} from 'styled-components';
import configuration from 'configuration';

let fadeHoldTimeout = 0;
let idleTimeout = 0;

const TransitionRoutes = ({children}: {children: React.ReactElement<RouteProps>}) => {
  const location = useLocation();
  const [displayLocation, setDisplayLocation] = useState(location);
  const [transitionStage, setTransitionStage] = useState<'fadeIn' | 'fadeOut' | undefined>(
    undefined,
  );

  const navRef = useRef(useNavigate());

  useEffect(() => {
    const timeout = configuration.idleTimeout;
    if (timeout > 0) {
      const resetIdleTimeout = () => {
        if (idleTimeout) window.clearTimeout(idleTimeout);
        idleTimeout = window.setTimeout(() => navRef.current('/'), timeout);
      };

      document.addEventListener('mousedown', resetIdleTimeout);
      return () => document.removeEventListener('mousedown', resetIdleTimeout);
    }
  }, []);

  useEffect(() => {
    if (location.pathname !== displayLocation.pathname) {
      if (fadeHoldTimeout) window.clearTimeout(fadeHoldTimeout);
      fadeHoldTimeout = 0;
      setTransitionStage('fadeOut');
    }
  }, [location, displayLocation]);

  const handleAnimationEnd = useCallback(() => {
    if (transitionStage === 'fadeOut') {
      setDisplayLocation(location);
      fadeHoldTimeout = window.setTimeout(() => setTransitionStage('fadeIn'), 300);
    } else setTransitionStage(undefined);
  }, [location, transitionStage]);

  return (
    <FadeScreen className={transitionStage} onAnimationEnd={handleAnimationEnd}>
      <Routes location={displayLocation}>{children}</Routes>
    </FadeScreen>
  );
};
export default TransitionRoutes;

const animFadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const animFadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

const FadeScreen = styled.div`
  &.fadeIn {
    animation: ${animFadeIn} 0.3s ease-in-out forwards;
  }

  &.fadeOut {
    animation: ${animFadeOut} 0.3s ease-in-out forwards;
  }
`;
